import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import { Box, styled } from '@mui/material';
import clsx from 'clsx';
import omit from 'lodash-es/omit';
import RcTextArea from 'rc-textarea';
import { textAreaClasses, getTextAreaStyle, textAreaClassPrefix, } from './style';
import { triggerFocus } from './utils';
import { triggerAriaMessage } from '../../Shared/a11yUtils';
import ClearButton from './ClearButton';
import { useTranslation } from '../../hooks/useTranslation';
const TextAreaBase = forwardRef((props, ref) => {
    const { sx, className, allowClear, readOnly, disabled, status, onChange, onPressEnter, onResize, onKeyDown, maxLength, showCount, showCountPosition = 'bottom-start', handlerRef, resizable, ...rest } = props;
    const { t } = useTranslation('Input');
    const rcTextAreaRef = useRef(null);
    useImperativeHandle(handlerRef, () => ({
        resizableTextArea: rcTextAreaRef.current?.resizableTextArea,
        focus: (option) => {
            triggerFocus(rcTextAreaRef.current?.resizableTextArea?.textArea, option);
        },
        blur: () => rcTextAreaRef.current?.blur(),
        setSelectionRange: (start, end, direction) => {
            const textarea = rcTextAreaRef.current?.resizableTextArea?.textArea;
            // istanbul ignore else
            if (textarea instanceof HTMLTextAreaElement) {
                textarea.setSelectionRange(start, end, direction);
            }
        },
    }));
    let mergedAllowClear;
    if (typeof allowClear === 'object' && allowClear?.clearIcon) {
        mergedAllowClear = allowClear;
    }
    else if (allowClear) {
        mergedAllowClear = {
            clearIcon: React.createElement(ClearButton, { className: textAreaClasses.clearIcon }),
        };
    }
    const handleKeyDown = (e) => {
        onKeyDown?.(e);
        if (maxLength &&
            !readOnly &&
            !disabled &&
            !e.ctrlKey &&
            !e.metaKey &&
            (e.nativeEvent.isComposing || e.key.length === 1)) {
            const { currentTarget } = e;
            setTimeout(() => {
                if (currentTarget.value.length >= maxLength) {
                    triggerAriaMessage(t('maxLengthMessage', { maxLength }));
                }
            }, 0);
        }
    };
    return (React.createElement(Box, { ref: ref, className: clsx(textAreaClasses.root, {
            [`${textAreaClasses.allowClear}`]: allowClear,
            [`${textAreaClasses.rootReadOnly}`]: readOnly,
            [`${textAreaClasses.rootShowCount}`]: showCount,
            [`${textAreaClasses.rootError}`]: status === 'error',
            [`${textAreaClasses.rootWarning}`]: status === 'warning',
        }, `${textAreaClasses.rootShowCountPosition}-${showCountPosition}`, className), sx: sx },
        React.createElement(RcTextArea, { ...rest, allowClear: mergedAllowClear, prefixCls: textAreaClassPrefix, classNames: {
                textarea: textAreaClasses.textArea,
                count: clsx(textAreaClasses.showCount, `${textAreaClasses.showCountPosition}-${showCountPosition}`),
            }, disabled: disabled, readOnly: readOnly, onResize: onResize, onChange: onChange, onPressEnter: onPressEnter, onKeyDown: handleKeyDown, ref: rcTextAreaRef, maxLength: maxLength, showCount: showCount })));
});
const StyledTextArea = styled(TextAreaBase)((props) => {
    const { theme, status, allowClear, showCount, size } = props;
    const shouldRenderAffix = !!allowClear || !!showCount;
    return getTextAreaStyle(theme, {
        status,
        shouldRenderAffix,
        size,
        resizable: props.autoSize ? false : (props.resizable ?? false),
    });
});
const RefTextArea = React.forwardRef((props, ref) => (React.createElement(StyledTextArea, { ...props, ref: ref })));
//  The exposed components must support the use of ref.
const TextArea = React.forwardRef((props, ref) => {
    const internalProps = omit(props, ['style']);
    return React.createElement(RefTextArea, { ...internalProps, ref: ref });
});
export default TextArea;
